import { Injectable } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { delay } from "rxjs/operators";
import { HelpConfig } from "src/app/core/config/help-config";
import { environment } from "src/environments/environment";
import {
  CertificadoDigitalModel,
  EcvFiscalConfigurationModel,
  EcvFiscalConfigurationUpdateRequest,
} from "src/app/pages/nfse/models/ecv-fiscal-configuration.model";
import { NFSE_STATUS_ID } from "src/app/pages/nfse/models/nfse-action.model";
import {
  NFSE_CAMPOS_CORRIGIVEIS_PADRAO,
  NfseCampoCorrigivel,
  NfseInvoiceEventModel,
  NfseInvoiceModel,
} from "src/app/pages/nfse/models/nfse-invoice.model";
import { NfseInvoiceSituationModel } from "src/app/pages/nfse/models/nfse-invoice-situation.model";
import { NfseAuditTrailModel } from "src/app/pages/nfse/models/nfse-audit-trail.model";
import { NfseStatus } from "src/app/pages/nfse/models/nfse-status.enum";
import { NfseRelatorioModel, NfseRelatorioRequest } from "src/app/pages/nfse/models/nfse-relatorio.model";
import {
  DataSourceFilter,
  DataSourceFilterDescriptor,
  DataSourceRequest,
} from "src/app/pages/nfse/models/datasource.model";

interface MockCorrecaoBody {
  motivo?: string;
  discriminacaoServico?: string;
  tomador?: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    razaoSocial?: string;
    razaoSocialOuNome?: string;
    cpfCnpj?: string;
    email?: string;
    telefone?: string;
    endereco?: string;
    descricaoCidade?: string;
    uf?: string;
  };
  servico?: { discriminacao?: string };
}

/**
 * Mock temporário de api/Nfse (listagens/detalhe ainda sem backend completo).
 * api/EcvFiscal NÃO é mockada — segue para a API real.
 * Ativo em local/dev/hmg/hmg2/hmg3/stage (environment.enableNfseMocks === true).
 * Nunca ativa em produção (environment.prod.ts tem enableNfseMocks: false).
 */
@Injectable()
export class NfseMockInterceptor implements HttpInterceptor {
  private readonly ECV_MOCK_1 = "ecv-mock-0000-0000-0000-000000000001";
  private readonly ECV_MOCK_2 = "ecv-mock-0000-0000-0000-000000000002";

  private invoices: NfseInvoiceModel[] = this.seedInvoices();
  private historicoMap: Record<string, NfseInvoiceEventModel[]> = {};
  private auditoriaMap: Record<string, NfseAuditTrailModel[]> = {};
  private ecvConfigs: Record<string, EcvFiscalConfigurationModel> = {};
  private certificados: Record<string, CertificadoDigitalModel> = {};

  constructor(private helpConfig: HelpConfig) {
    this.seedHistoricoEAuditoria();
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.mocksAtivos) {
      return next.handle(req);
    }

    if (this.isNfseUrl(req.url)) {
      return this.handleNfse(req, this.nfsePath(req.url));
    }

    return next.handle(req);
  }

  /** Controlado por environment.enableNfseMocks — defina false para testar a API real. */
  private get mocksAtivos(): boolean {
    return environment.enableNfseMocks === true;
  }

  private isNfseUrl(url: string): boolean {
    return /\/nfse(\/|\?|$)/i.test(url);
  }

  private nfsePath(url: string): string {
    const match = url.match(/\/nfse\/?(.*)$/i);
    return match ? match[1].split("?")[0] : "";
  }

  // ───────────────────────── api/EcvFiscal ─────────────────────────

  private handleEcvFiscal(req: HttpRequest<unknown>, path: string): Observable<HttpEvent<unknown>> {
    const segments = path.split("/").filter(Boolean);

    if (segments[0] === "certificado") {
      const idEcv = segments[1];

      if (req.method === "POST") {
        const formData = req.body as FormData;
        const arquivo = formData?.get("arquivo") as File | null;
        const certificado: CertificadoDigitalModel = {
          idCertificado: this.uuid(),
          nomeArquivo: arquivo?.name || "certificado-mock.pfx",
          dataEnvio: new Date().toISOString(),
          dataValidade: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365).toISOString(),
          diasParaExpirar: 365,
          alertaExpiracaoAtivo: false,
        };
        this.certificados[idEcv] = certificado;
        return this.ok(certificado);
      }

      if (req.method === "GET") {
        return this.ok(this.certificados[idEcv] ?? { existe: false });
      }

      if (req.method === "DELETE") {
        delete this.certificados[idEcv];
        return this.ok({ removido: true });
      }
    }

    const idEcv = segments[0];

    if (req.method === "GET") {
      return this.ok(this.ecvConfigs[idEcv] ?? { existe: false, idEcv });
    }

    if (req.method === "PUT") {
      const existente = this.ecvConfigs[idEcv];
      const body = (req.body || {}) as EcvFiscalConfigurationUpdateRequest & {
        codigoMunicipioIbgeEmissor?: string;
        cnaeOuCodigoServico?: string;
        itemServicoLC116?: string;
        optanteSimplesNacional?: boolean;
      };
      const camposPendentes = Object.entries(body)
        .filter(([, value]) => value === null || value === undefined || value === "")
        .map(([campo]) => campo);

      const atualizado: EcvFiscalConfigurationModel = {
        idEcvFiscalConfiguration: existente?.idEcvFiscalConfiguration ?? this.uuid(),
        idEcv,
        cnpj: body.cnpj,
        razaoSocial: body.razaoSocial,
        nomeFantasia: body.nomeFantasia ?? null,
        inscricaoMunicipal: body.inscricaoMunicipal,
        inscricaoEstadual: body.inscricaoEstadual ?? null,
        codigoIbgeMunicipio:
          body.codigoIbgeMunicipio ?? body.codigoMunicipioIbgeEmissor ?? "",
        idTaxRegime: Number(body.idTaxRegime ?? 0),
        idSpecialTaxRegime: Number(body.idSpecialTaxRegime ?? 0),
        codigoCnae: body.codigoCnae ?? body.cnaeOuCodigoServico ?? "",
        idLc116: body.idLc116 ?? body.itemServicoLC116 ?? "",
        codigoNbs: body.codigoNbs ?? null,
        aliquotaIss: Number(body.aliquotaIss ?? 0),
        simplesNacional: !!(body.simplesNacional ?? body.optanteSimplesNacional),
        email: body.email ?? "",
        telefone: body.telefone ?? "",
        certificado: this.certificados[idEcv] ?? null,
        municipioSuportado: true,
        camposAdicionaisExigidos: [],
        sincronizadoComProvedor: camposPendentes.length === 0,
        camposPendentes,
        dataUltimaAlteracao: new Date().toISOString(),
        usuarioUltimaAlteracao: "Usuário Mock",
      };

      this.ecvConfigs[idEcv] = atualizado;
      return this.ok(atualizado);
    }

    return this.notFound();
  }

  // ───────────────────────── api/Nfse ─────────────────────────

  private handleNfse(req: HttpRequest<unknown>, path: string): Observable<HttpEvent<unknown>> {
    const segments = path.split("/").filter(Boolean);

    if (path === "list" && req.method === "POST") {
      return this.ok(this.buildListResult(req.body as DataSourceRequest));
    }

    if (path === "relatorio" && req.method === "POST") {
      return this.ok(this.buildRelatorio(req.body as NfseRelatorioRequest & { dataInicio?: string; dataFim?: string }));
    }

    if (segments[0] === "historico") {
      this.resolveInvoice(segments[1]);
      return this.ok(this.historicoMap[segments[1]] ?? []);
    }

    if (segments[0] === "auditoria") {
      this.resolveInvoice(segments[1]);
      return this.ok(this.auditoriaMap[segments[1]] ?? []);
    }

    if (segments[0] === "xml") {
      return this.mockXml(segments[1]);
    }

    if (segments[0] === "pdf") {
      return this.mockPdf(segments[1]);
    }

    if (segments[0] === "reprocessar" && req.method === "POST") {
      const reprocessada = this.reprocessar(segments[1], req.body as MockCorrecaoBody | null);
      if (!reprocessada) {
        return this.error(404, "NFS-e não encontrada.");
      }
      return this.ok(
        this.toActionResponse(reprocessada, "Reprocessamento solicitado ao provedor fiscal.")
      );
    }

    if (segments[0] === "reenviar" && req.method === "POST") {
      return this.ok(this.transition(segments[1], "Reenviada"));
    }

    if (segments[0] === "cancelar" && req.method === "POST") {
      return this.handleCancelar(segments[1], req.body as { motivo?: string } | null);
    }

    if (segments[1] === "cancelar" && req.method === "POST") {
      return this.handleCancelar(segments[0], req.body as { motivo?: string } | null);
    }

    if (segments[0] === "cancelamento" && segments[1] === "status") {
      return this.ok(this.resolverCancelamento(segments[2]));
    }

    if (segments[0] === "dados" && req.method === "PUT") {
      return this.ok(this.corrigirDados(segments[1], req.body as MockCorrecaoBody | null));
    }

    if (segments[0] === "substituir" && req.method === "POST") {
      return this.ok(this.substituir(segments[1], req.body));
    }

    if (segments.length === 1 && req.method === "GET") {
      return this.ok(this.getOne(segments[0]));
    }

    return this.notFound();
  }

  // ───────────────────────── regras/estado do mock ─────────────────────────

  /** GET nfse/{idInspection} — contrato NfseInvoiceSituationModel. */
  private getOne(idInspection: string): NfseInvoiceSituationModel {
    const invoice = this.resolveInvoice(idInspection);
    if (!invoice) {
      return this.semNotaResponse(idInspection);
    }
    return this.toSituation(invoice);
  }

  private semNotaResponse(idInspection: string): NfseInvoiceSituationModel {
    const hash = this.hash(idInspection);
    return {
      possuiNfse: false,
      status: "NaoIniciada",
      statusDescricao: "NFS-e ainda nao iniciada.",
      motivoInelegibilidade:
        hash < 0.07 ? "ECV sem configuração fiscal cadastrada." : null,
      xmlDisponivel: false,
      pdfDisponivel: false,
    };
  }

  private toSituation(invoice: NfseInvoiceModel): NfseInvoiceSituationModel {
    return {
      possuiNfse: true,
      status: invoice.status,
      statusDescricao: invoice.status,
      motivoInelegibilidade: null,
      numero: invoice.numero,
      serie: invoice.serie,
      codigoVerificacao: invoice.codigoVerificacao,
      dataEmissao: invoice.dataEmissao,
      valor: invoice.valorServico,
      xmlDisponivel: invoice.documentosDisponiveis?.xmlDisponivel ?? false,
      pdfDisponivel: invoice.documentosDisponiveis?.pdfDisponivel ?? false,
      motivoRejeicao: invoice.mensagemRejeicao,
      dataCancelamento: invoice.dataCancelamento,
      motivoCancelamento: invoice.motivoCancelamento,
    };
  }

  private statusToId(status: NfseStatus): number {
    const found = Object.entries(NFSE_STATUS_ID).find(([, name]) => name === status);
    return found ? Number(found[0]) : 0;
  }

  private toActionResponse(invoice: NfseInvoiceModel, mensagem: string) {
    return {
      id: invoice.idNfseInvoice,
      idInspection: invoice.idInspection,
      status: this.statusToId(invoice.status),
      protocolo: invoice.protocolo,
      mensagem,
    };
  }

  /**
   * Gera (e mantém, dentro da sessão) uma NFS-e mock para qualquer idInspection
   * real que a tela consultar — assim a aba "NFS-e" da Vistoria mostra dados de
   * verdade para qualquer vistoria aberta, não só para os 8 ids fictícios acima.
   * Um recorte fica sem nota (estado "NaoIniciada"), pra também validar esse caso.
   */
  private resolveInvoice(idInspection: string): NfseInvoiceModel | null {
    const existente = this.invoices.find((i) => i.idInspection === idInspection);
    if (existente) {
      return existente;
    }

    const hash = this.hash(idInspection);

    if (hash < 0.15) {
      return null;
    }

    const statusPossiveis: NfseStatus[] = [
      "Autorizada",
      "Autorizada",
      "Autorizada",
      "EmissaoSolicitada",
      "Rejeitada",
      "PendenteCorrecao",
      "EmReprocessamento",
    ];
    const status = statusPossiveis[Math.floor(hash * statusPossiveis.length)];
    const autorizada = status === "Autorizada";

    const novaInvoice: NfseInvoiceModel = {
      idNfseInvoice: this.uuid(),
      idInspection,
      idEcv: hash < 0.5 ? this.ECV_MOCK_1 : this.ECV_MOCK_2,
      status,
      numero: autorizada ? String(2000 + Math.floor(hash * 999)) : null,
      serie: autorizada ? "1" : null,
      codigoVerificacao: autorizada ? this.uuid().slice(0, 8).toUpperCase() : null,
      protocolo: autorizada ? this.uuid() : null,
      dataAbertura: new Date().toISOString(),
      dataEmissao: autorizada ? new Date().toISOString() : null,
      serviceOrder: String(100000000 + Math.floor(hash * 99999999)),
      placa: `MOCK${String(Math.floor(hash * 9999)).padStart(4, "0")}`,
      valorServico: 150 + Math.round(hash * 300),
      tomador: {
        razaoSocialOuNome: "Contratante Mock",
        cpfCnpj: "98765432100",
        email: "contratante.mock@example.com",
        endereco: "Av. Mock, 456",
        municipio: "São Paulo",
        uf: "SP",
        telefone: "11988887777",
      },
      discriminacaoServico: "Vistoria veicular — inspeção de segurança veicular",
      mensagemRejeicao: status === "Rejeitada" || status === "PendenteCorrecao" ? "Dados do tomador inconsistentes com a base do provedor." : null,
      camposCorrigiveis:
        status === "Rejeitada" || status === "PendenteCorrecao"
          ? (["cpfCnpj", "email", "endereco"] as NfseCampoCorrigivel[])
          : status === "FalhaIntegracao" || status === "EmReprocessamento"
            ? [...NFSE_CAMPOS_CORRIGIVEIS_PADRAO]
            : [],
      dataCancelamento: null,
      motivoCancelamento: null,
      linkNota: null,
      documentosDisponiveis: { xmlDisponivel: autorizada, pdfDisponivel: autorizada },
    };

    this.invoices.push(novaInvoice);
    this.pushHistorico(idInspection, status, "EmPreparacao");
    this.pushAuditoria(idInspection, novaInvoice.idNfseInvoice, "EmPreparacao", status, "Emissao");

    return novaInvoice;
  }

  private hash(value: string): number {
    let h = 0;
    for (let i = 0; i < value.length; i++) {
      h = (h * 31 + value.charCodeAt(i)) >>> 0;
    }
    return (h % 1000) / 1000;
  }

  private buildListResult(request: DataSourceRequest) {
    const rootFilter = request?.filter;
    let result = [...this.invoices];

    if (rootFilter) {
      result = result.filter((invoice) => this.matchesFilter(invoice, rootFilter));
    }

    const total = result.length;
    const skip = request?.skip || 0;
    const take = request?.take || 10;
    const data = result.slice(skip, skip + take).map((invoice) => this.toListItem(invoice));

    return { data, total, errors: null };
  }

  private toListItem(invoice: NfseInvoiceModel) {
    return {
      id: invoice.idNfseInvoice,
      idInspection: invoice.idInspection,
      idEcv: invoice.idEcv,
      numeroOS: invoice.serviceOrder,
      idNfseInvoiceStatus: this.statusToId(invoice.status),
      status: invoice.status,
      statusDescricao: invoice.status,
      numero: invoice.numero,
      serie: invoice.serie,
      issuedAt: invoice.dataEmissao,
      createdAt: invoice.dataAbertura,
      serviceValue: invoice.valorServico,
      rejectionMessage: invoice.mensagemRejeicao,
    };
  }

  private matchesFilter(invoice: NfseInvoiceModel, filter: DataSourceFilter | DataSourceFilterDescriptor | null | undefined): boolean {
    if (!filter) {
      return true;
    }

    if ("logic" in filter && filter.logic && Array.isArray(filter.filters)) {
      if (filter.logic === "or") {
        return filter.filters.some((f) => this.matchesFilter(invoice, f));
      }
      return filter.filters.every((f) => this.matchesFilter(invoice, f));
    }

    const descriptor = filter as DataSourceFilterDescriptor;
    const fieldValue = invoice[descriptor.field as keyof NfseInvoiceModel];
    const filterValue = descriptor.value;

    switch (descriptor.operator) {
      case "in":
        return Array.isArray(filterValue) && filterValue.includes(fieldValue);
      case "eq":
        if (descriptor.field === "idEcv" && filterValue) {
          return this.invoicesPorEcv(String(filterValue)).some(
            (item) => item.idInspection === invoice.idInspection
          );
        }
        return fieldValue === filterValue;
      case "contains": {
        if (fieldValue == null || filterValue == null) {
          return false;
        }
        return String(fieldValue)
          .toLowerCase()
          .includes(String(filterValue).toLowerCase());
      }
      case "gte":
        return this.compareValues(fieldValue, filterValue) >= 0;
      case "lte":
        return this.compareValues(fieldValue, filterValue) <= 0;
      default:
        return true;
    }
  }

  private compareValues(left: unknown, right: unknown): number {
    if (left == null || right == null) {
      return left == null ? -1 : 1;
    }
    const leftValue =
      left instanceof Date || this.isDateLike(left)
        ? new Date(left as string | Date).getTime()
        : left;
    const rightValue =
      right instanceof Date || this.isDateLike(right)
        ? new Date(right as string | Date).getTime()
        : right;
    if (leftValue < rightValue) {
      return -1;
    }
    if (leftValue > rightValue) {
      return 1;
    }
    return 0;
  }

  private isDateLike(value: unknown): boolean {
    return typeof value === "string" && !Number.isNaN(Date.parse(value));
  }

  private buildRelatorio(request: NfseRelatorioRequest & { dataInicio?: string; dataFim?: string }): NfseRelatorioModel {
    const dataInicial = request?.dataInicial ?? request?.dataInicio;
    const dataFinal = request?.dataFinal ?? request?.dataFim;
    const origem = this.invoicesNoPeriodo(
      this.invoicesPorEcv(request?.idEcv),
      dataInicial,
      dataFinal
    );

    const totais = this.totaisRelatorio(origem);
    const valorTotal = origem.reduce((acc, i) => acc + i.valorServico, 0);

    const porEcvMap = new Map<string, { razaoSocial: string; itens: NfseInvoiceModel[] }>();
    origem.forEach((i) => {
      const razaoSocial = i.idEcv === this.ECV_MOCK_1 ? "ECV Mock Um LTDA" : "ECV Mock Dois LTDA";
      if (!porEcvMap.has(i.idEcv)) {
        porEcvMap.set(i.idEcv, { razaoSocial, itens: [] });
      }
      porEcvMap.get(i.idEcv).itens.push(i);
    });

    const porEcv = Array.from(porEcvMap.entries()).map(([idEcv, { razaoSocial, itens }]) => ({
      idEcv,
      razaoSocial,
      ...this.totaisRelatorio(itens),
      valorTotal: itens.reduce((acc, i) => acc + i.valorServico, 0),
    }));

    return {
      periodo: { dataInicio: dataInicial, dataFim: dataFinal },
      totais,
      totalNotas: origem.length,
      valorTotal,
      porEcv,
      evolucao: this.evolucaoRelatorio(origem, dataInicial, dataFinal),
      falhas: this.falhasRelatorio(origem),
    };
  }

  private invoicesNoPeriodo(
    itens: NfseInvoiceModel[],
    dataInicio?: string,
    dataFim?: string
  ): NfseInvoiceModel[] {
    if (!dataInicio && !dataFim) {
      return itens;
    }

    const inicio = dataInicio ? this.inicioDoDia(new Date(dataInicio)).getTime() : null;
    const fim = dataFim ? this.fimDoDia(new Date(dataFim)).getTime() : null;

    return itens.filter((invoice) => {
      const raw = invoice.dataEmissao || invoice.dataAbertura;
      if (!raw) {
        return false;
      }
      const tempo = new Date(raw).getTime();
      if (inicio != null && tempo < inicio) {
        return false;
      }
      if (fim != null && tempo > fim) {
        return false;
      }
      return true;
    });
  }

  private invoicesPorEcv(idEcv?: string): NfseInvoiceModel[] {
    if (!idEcv) {
      return [...this.invoices];
    }

    const direto = this.invoices.filter((invoice) => invoice.idEcv === idEcv);
    if (direto.length) {
      return direto;
    }

    const mockId =
      this.hashString(idEcv) % 2 === 0 ? this.ECV_MOCK_1 : this.ECV_MOCK_2;
    return this.invoices.filter((invoice) => invoice.idEcv === mockId);
  }

  private hashString(value: string): number {
    return value.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  }

  private totaisRelatorio(itens: NfseInvoiceModel[]) {
    return {
      emitidas: itens.filter((i) => i.status === "Autorizada").length,
      rejeitadas: itens.filter((i) => i.status === "Rejeitada").length,
      pendentes: itens.filter((i) =>
        ["EmPreparacao", "EmissaoSolicitada", "PendenteCorrecao", "EmReprocessamento"].includes(i.status)
      ).length,
      canceladas: itens.filter((i) => i.status === "Cancelada").length,
      falhaEnvio: itens.filter((i) => i.status === "FalhaEnvioEmail" || i.status === "FalhaIntegracao").length,
    };
  }

  private evolucaoRelatorio(
    itens: NfseInvoiceModel[],
    dataInicio?: string,
    dataFim?: string
  ) {
    const fim = this.inicioDoDia(dataFim ? new Date(dataFim) : new Date());
    const inicio = dataInicio
      ? this.inicioDoDia(new Date(dataInicio))
      : this.inicioDoDia(new Date(fim.getTime() - 29 * 24 * 60 * 60 * 1000));

    const porDia = new Map<string, { autorizadas: number; rejeitadas: number }>();
    itens.forEach((invoice) => {
      const raw = invoice.dataEmissao || invoice.dataAbertura;
      if (!raw) {
        return;
      }
      const chave = this.chaveDia(new Date(raw));
      const bucket = porDia.get(chave) || { autorizadas: 0, rejeitadas: 0 };
      if (
        invoice.status === "Autorizada" ||
        invoice.status === "EnviadaPorEmail" ||
        invoice.status === "Reenviada"
      ) {
        bucket.autorizadas += 1;
      }
      if (invoice.status === "Rejeitada") {
        bucket.rejeitadas += 1;
      }
      porDia.set(chave, bucket);
    });

    const pontos: { data: string; autorizadas: number; rejeitadas: number }[] = [];
    const cursor = new Date(inicio);
    while (cursor.getTime() <= fim.getTime() && pontos.length < 62) {
      const chave = this.chaveDia(cursor);
      const bucket = porDia.get(chave) || { autorizadas: 0, rejeitadas: 0 };
      pontos.push({ data: chave, autorizadas: bucket.autorizadas, rejeitadas: bucket.rejeitadas });
      cursor.setDate(cursor.getDate() + 1);
    }

    if (pontos.some((p) => p.autorizadas || p.rejeitadas)) {
      return pontos;
    }

    const totais = this.totaisRelatorio(itens);
    if (!totais.emitidas && !totais.rejeitadas) {
      return [];
    }

    return pontos.map((ponto, index) => ({
      data: ponto.data,
      autorizadas: Math.max(
        0,
        Math.round((totais.emitidas / Math.max(pontos.length, 1)) * (1 + Math.sin(index / 2)))
      ),
      rejeitadas: Math.max(
        0,
        Math.round((totais.rejeitadas / Math.max(pontos.length, 1)) * (1 + Math.cos(index / 3)))
      ),
    }));
  }

  private falhasRelatorio(itens: NfseInvoiceModel[]) {
    const agrupado = new Map<string, number>();

    itens.forEach((invoice) => {
      const motivo = (invoice.mensagemRejeicao || "").trim();
      if (!motivo) {
        return;
      }
      agrupado.set(motivo, (agrupado.get(motivo) || 0) + 1);
    });

    const falhas = Array.from(agrupado.entries())
      .map(([motivo, quantidade]) => ({ motivo, quantidade }))
      .sort(
        (a, b) =>
          b.quantidade - a.quantidade || a.motivo.localeCompare(b.motivo, "pt-BR")
      );

    if (falhas.length && falhas.some((falha) => falha.quantidade > 1)) {
      return falhas;
    }

    if (!falhas.length) {
      return [];
    }

    const pesos = [8, 5, 3, 2, 1];
    return falhas.map((falha, index) => ({
      motivo: falha.motivo,
      quantidade: pesos[index] ?? 1,
    }));
  }

  private inicioDoDia(data: Date): Date {
    const dia = new Date(data);
    dia.setHours(0, 0, 0, 0);
    return dia;
  }

  private fimDoDia(data: Date): Date {
    const dia = new Date(data);
    dia.setHours(23, 59, 59, 999);
    return dia;
  }

  private dataIso(diasAtras: number, hora = "10:00:00.000Z"): string {
    const dia = new Date();
    dia.setDate(dia.getDate() - diasAtras);
    const ano = dia.getFullYear();
    const mes = String(dia.getMonth() + 1).padStart(2, "0");
    const d = String(dia.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${d}T${hora}`;
  }

  private chaveDia(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const dia = String(data.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${dia}`;
  }

  private transition(idInspection: string, status: NfseStatus, extra: Partial<NfseInvoiceModel> = {}): NfseInvoiceModel {
    const invoice = this.invoices.find((i) => i.idInspection === idInspection);
    if (!invoice) {
      return null;
    }

    const statusAnterior = invoice.status;
    Object.assign(invoice, extra, { status });

    this.pushHistorico(idInspection, status, statusAnterior);
    this.pushAuditoria(idInspection, invoice.idNfseInvoice, statusAnterior, status);

    return invoice;
  }

  private podeCancelar(idInspection: string) {
    const invoice = this.resolveInvoice(idInspection);
    if (!invoice) {
      return {
        permitido: false,
        motivo: "NFS-e não encontrada.",
      };
    }

    const statusPermite = [
      "Autorizada",
      "EnviadaPorEmail",
      "Reenviada",
      "FalhaEnvioEmail",
    ];

    if (!statusPermite.includes(invoice.status)) {
      return {
        permitido: false,
        motivo: `Cancelamento não permitido para o status "${invoice.status}".`,
      };
    }

    return { permitido: true, motivo: null };
  }

  private handleCancelar(idInspection: string, body: { motivo?: string } | null) {
    const motivo = String(body?.motivo || "").trim();
    if (!motivo) {
      return this.error(400, "Informe o motivo do cancelamento.");
    }

    const checagem = this.podeCancelar(idInspection);
    if (!checagem.permitido) {
      return this.error(400, checagem.motivo || "Cancelamento não permitido.");
    }

    const atualizada = this.transition(idInspection, "CancelamentoSolicitado", {
      motivoCancelamento: motivo,
    });
    if (!atualizada) {
      return this.error(404, "NFS-e não encontrada.");
    }
    return this.ok(
      this.toActionResponse(atualizada, "Cancelamento solicitado ao provedor fiscal.")
    );
  }

  private resolverCancelamento(idInspection: string) {
    const invoice = this.invoices.find((i) => i.idInspection === idInspection);
    if (!invoice) {
      return {
        id: this.uuid(),
        idInspection,
        status: this.statusToId("NaoIniciada"),
        protocolo: null,
        cancellationReason: null,
        cancelledAt: null,
        mensagem: "NFS-e não encontrada para consulta de cancelamento.",
      };
    }

    if (invoice.status === "CancelamentoSolicitado") {
      invoice.status = "Cancelada";
      invoice.dataCancelamento = new Date().toISOString();
      this.pushHistorico(idInspection, "Cancelada");
      this.pushAuditoria(idInspection, invoice.idNfseInvoice, "CancelamentoSolicitado", "Cancelada");
    }

    return {
      id: invoice.idNfseInvoice,
      idInspection: invoice.idInspection,
      status: this.statusToId(invoice.status),
      protocolo: invoice.protocolo,
      cancellationReason: invoice.motivoCancelamento ?? null,
      cancelledAt: invoice.dataCancelamento ?? null,
      mensagem:
        invoice.status === "Cancelada"
          ? "Cancelamento confirmado pelo provedor fiscal."
          : "Consulta de status de cancelamento.",
    };
  }

  private reprocessar(idInspection: string, body: MockCorrecaoBody | null): NfseInvoiceModel | null {
    const invoice = this.invoices.find((i) => i.idInspection === idInspection);
    if (!invoice) {
      return null;
    }

    if (body?.tomador) {
      invoice.tomador = {
        ...invoice.tomador,
        ...Object.fromEntries(Object.entries(body.tomador).filter(([, v]) => v != null)),
      };
    }
    if (body?.discriminacaoServico != null) {
      invoice.discriminacaoServico = body.discriminacaoServico;
    }

    const cpfCnpj = String(invoice.tomador?.cpfCnpj || "").replace(/\D/g, "");
    const email = String(invoice.tomador?.email || "").trim();
    const cpfCnpjValido = cpfCnpj.length === 11 || cpfCnpj.length === 14;
    const emailValido = email.includes("@") && email.includes(".");

    if (!cpfCnpjValido) {
      return this.transition(idInspection, "Rejeitada", {
        mensagemRejeicao: "CPF/CNPJ do tomador inválido.",
        camposCorrigiveis: ["cpfCnpj"] as NfseCampoCorrigivel[],
        documentosDisponiveis: { xmlDisponivel: false, pdfDisponivel: false },
        numero: null,
        serie: null,
        codigoVerificacao: null,
        protocolo: null,
      });
    }

    if (!emailValido) {
      return this.transition(idInspection, "Rejeitada", {
        mensagemRejeicao: "E-mail do tomador inválido ou não informado.",
        camposCorrigiveis: ["email"] as NfseCampoCorrigivel[],
        documentosDisponiveis: { xmlDisponivel: false, pdfDisponivel: false },
        numero: null,
        serie: null,
        codigoVerificacao: null,
        protocolo: null,
      });
    }

    return this.transition(idInspection, "Autorizada", {
      mensagemRejeicao: null,
      camposCorrigiveis: [],
      numero: invoice.numero || String(2000 + Math.floor(Math.random() * 999)),
      serie: invoice.serie || "1",
      codigoVerificacao: invoice.codigoVerificacao || this.uuid().slice(0, 8).toUpperCase(),
      protocolo: invoice.protocolo || this.uuid(),
      dataEmissao: new Date().toISOString(),
      documentosDisponiveis: { xmlDisponivel: true, pdfDisponivel: true },
    });
  }

  private corrigirDados(idInspection: string, body: MockCorrecaoBody | null): NfseInvoiceModel {
    const invoice = this.invoices.find((i) => i.idInspection === idInspection);
    if (!invoice) {
      return null;
    }

    if (body?.tomador) {
      const t = body.tomador;
      const enderecoPartes = [t.logradouro, t.numero, t.complemento, t.bairro]
        .map((v) => String(v || "").trim())
        .filter(Boolean);

      invoice.tomador = {
        ...invoice.tomador,
        razaoSocialOuNome:
          t.razaoSocial ?? t.razaoSocialOuNome ?? invoice.tomador.razaoSocialOuNome,
        cpfCnpj: t.cpfCnpj ?? invoice.tomador.cpfCnpj,
        email: t.email ?? invoice.tomador.email,
        telefone: t.telefone ?? invoice.tomador.telefone,
        endereco:
          enderecoPartes.length > 0
            ? enderecoPartes.join(", ")
            : t.endereco ?? invoice.tomador.endereco,
        municipio: t.descricaoCidade ?? invoice.tomador.municipio,
        uf: t.uf ?? invoice.tomador.uf,
      };
    }

    const discriminacao =
      body?.servico?.discriminacao ?? body?.discriminacaoServico;
    if (discriminacao != null) {
      invoice.discriminacaoServico = discriminacao;
    }

    this.pushAuditoria(idInspection, invoice.idNfseInvoice, invoice.status, invoice.status, "CorrecaoDados");
    return invoice;
  }

  private substituir(idInspection: string, body: unknown): NfseInvoiceModel & { idNfseInvoiceSubstituida: string } {
    const original = this.invoices.find((i) => i.idInspection === idInspection);
    if (!original) {
      return null;
    }

    const idNfseInvoiceSubstituida = original.idNfseInvoice;
    const nova: NfseInvoiceModel = {
      ...original,
      idNfseInvoice: this.uuid(),
      status: "Autorizada",
      numero: String(Number(original.numero || "1000") + 1),
      protocolo: this.uuid(),
      codigoVerificacao: this.uuid().slice(0, 8).toUpperCase(),
      dataEmissao: new Date().toISOString(),
    };

    const index = this.invoices.findIndex((i) => i.idInspection === idInspection);
    this.invoices[index] = nova;

    this.pushHistorico(idInspection, "Autorizada");
    this.pushAuditoria(idInspection, nova.idNfseInvoice, original.status, "Autorizada", "Emissao");

    return { ...nova, idNfseInvoiceSubstituida };
  }

  private mockXml(idInspection: string): Observable<HttpEvent<unknown>> {
    const invoice = this.invoices.find((i) => i.idInspection === idInspection);
    if (!invoice?.documentosDisponiveis?.xmlDisponivel) {
      return this.error(409, "XML ainda não disponível para esta nota.");
    }
    const xml = `<?xml version="1.0" encoding="UTF-8"?><nfse mock="true"><numero>${invoice.numero}</numero></nfse>`;
    return of(new HttpResponse({ status: 200, body: xml })).pipe(delay(300));
  }

  private mockPdf(idInspection: string): Observable<HttpEvent<unknown>> {
    const invoice = this.invoices.find((i) => i.idInspection === idInspection);
    if (!invoice?.documentosDisponiveis?.pdfDisponivel) {
      return this.error(409, "PDF ainda não disponível para esta nota.");
    }
    const blob = new Blob([`%PDF-1.4 mock nota ${invoice.numero}`], { type: "application/pdf" });
    return of(new HttpResponse({ status: 200, body: blob })).pipe(delay(300));
  }

  private pushHistorico(
    idInspection: string,
    statusNovo: NfseStatus,
    statusAnterior: NfseStatus | null = null
  ) {
    const lista = this.historicoMap[idInspection] || (this.historicoMap[idInspection] = []);
    const anterior =
      statusAnterior ??
      ((lista.length > 0 ? lista[lista.length - 1].statusNovo : null) as NfseStatus | null);
    lista.push({
      dataHora: new Date().toISOString(),
      tipoEvento: statusNovo,
      descricaoEvento: `Status atualizado para ${statusNovo}`,
      statusAnterior: anterior,
      statusNovo,
      responsavel: "Usuário Mock",
      retornoProvedor: null,
      statusBrutoProvedor: null,
    });
  }

  private pushAuditoria(
    idInspection: string,
    _idNfseInvoice: string,
    statusAnterior: NfseStatus,
    statusNovo: NfseStatus,
    tipoEvento = "Reprocessamento",
    extra: {
      dataHora?: string;
      origem?: string;
      responsavel?: string;
      usuarioResponsavel?: string;
      perfil?: string | null;
      perfilUsuario?: string | null;
      evidencia?: string | null;
      evidenciaAssociada?: unknown;
      retornoProvedor?: string | null;
    } = {}
  ) {
    const lista = this.auditoriaMap[idInspection] || (this.auditoriaMap[idInspection] = []);
    lista.push({
      dataHora: extra.dataHora ?? new Date().toISOString(),
      origem: extra.origem ?? "Evento",
      tipoEvento,
      responsavel: extra.responsavel ?? extra.usuarioResponsavel ?? "Sistema",
      perfil: extra.perfil ?? extra.perfilUsuario ?? null,
      statusAnterior,
      statusNovo,
      evidencia: extra.evidencia ?? extra.retornoProvedor ?? "",
    });
  }

  private seedInvoices(): NfseInvoiceModel[] {
    const base = (
      idInspection: string,
      status: NfseStatus,
      idEcv: string,
      overrides: Partial<NfseInvoiceModel> = {}
    ): NfseInvoiceModel => ({
      idNfseInvoice: this.uuid(),
      idInspection,
      idEcv,
      status,
      numero: null,
      serie: null,
      codigoVerificacao: null,
      protocolo: null,
      dataAbertura: this.dataIso(12, "08:00:00.000Z"),
      dataEmissao: null,
      serviceOrder: "000123456",
      placa: "ABC1D23",
      valorServico: 195,
      tomador: {
        razaoSocialOuNome: "João da Silva Mock",
        cpfCnpj: "12345678900",
        email: "joao.mock@example.com",
        endereco: "Rua Mock, 123",
        municipio: "São Paulo",
        uf: "SP",
        telefone: "11999990000",
      },
      discriminacaoServico: "Vistoria veicular — inspeção de segurança veicular",
      mensagemRejeicao: null,
      camposCorrigiveis: [],
      dataCancelamento: null,
      motivoCancelamento: null,
      linkNota: null,
      documentosDisponiveis: { xmlDisponivel: false, pdfDisponivel: false },
      ...overrides,
    });

    return [
      base("insp-mock-0000-0000-0000-000000000001", "Autorizada", this.ECV_MOCK_1, {
        numero: "1001",
        serie: "1",
        protocolo: this.uuid(),
        codigoVerificacao: "ABC12345",
        dataEmissao: this.dataIso(10),
        serviceOrder: "000111111",
        placa: "ABC1D23",
        documentosDisponiveis: { xmlDisponivel: true, pdfDisponivel: true },
      }),
      base("insp-mock-0000-0000-0000-000000000002", "Rejeitada", this.ECV_MOCK_1, {
        mensagemRejeicao: "Erro ao montar nota: valor inválido para o campo Tipo:Tributacao.",
        camposCorrigiveis: ["discriminacaoServico"],
        serviceOrder: "000222222",
        placa: "DEF2E45",
      }),
      base("insp-mock-0000-0000-0000-000000000003", "PendenteCorrecao", this.ECV_MOCK_2, {
        mensagemRejeicao: "CPF/CNPJ do tomador inválido.",
        camposCorrigiveis: ["cpfCnpj"],
        tomador: {
          razaoSocialOuNome: "João da Silva Mock",
          cpfCnpj: "123",
          email: "joao.mock@example.com",
          endereco: "Rua Mock, 123",
          municipio: "São Paulo",
          uf: "SP",
          telefone: "11999990000",
        },
        serviceOrder: "000333333",
        placa: "GHI3F67",
      }),
      base("insp-mock-0000-0000-0000-000000000004", "EmReprocessamento", this.ECV_MOCK_1, {
        camposCorrigiveis: [...NFSE_CAMPOS_CORRIGIVEIS_PADRAO],
        mensagemRejeicao: "Aguardando retorno do provedor fiscal na tentativa anterior.",
        serviceOrder: "000444444",
        placa: "JKL4G89",
      }),
      base("insp-mock-0000-0000-0000-000000000005", "CancelamentoSolicitado", this.ECV_MOCK_2, {
        numero: "1002",
        dataEmissao: this.dataIso(8),
        serviceOrder: "000555555",
        placa: "MNO5H01",
        documentosDisponiveis: { xmlDisponivel: true, pdfDisponivel: true },
      }),
      base("insp-mock-0000-0000-0000-000000000006", "Cancelada", this.ECV_MOCK_1, {
        numero: "1003",
        dataEmissao: this.dataIso(20),
        dataCancelamento: this.dataIso(19),
        motivoCancelamento: "Vistoria cancelada pelo cliente.",
        serviceOrder: "000666666",
        placa: "PQR6I23",
        documentosDisponiveis: { xmlDisponivel: true, pdfDisponivel: false },
      }),
      base("insp-mock-0000-0000-0000-000000000007", "EnviadaPorEmail", this.ECV_MOCK_2, {
        numero: "1004",
        dataEmissao: this.dataIso(6),
        serviceOrder: "000777777",
        placa: "STU7J45",
        documentosDisponiveis: { xmlDisponivel: true, pdfDisponivel: true },
      }),
      base("insp-mock-0000-0000-0000-000000000008", "FalhaIntegracao", this.ECV_MOCK_1, {
        mensagemRejeicao: "Falha de comunicação com o provedor fiscal (timeout).",
        camposCorrigiveis: [...NFSE_CAMPOS_CORRIGIVEIS_PADRAO],
        serviceOrder: "000888888",
        placa: "VWX8K67",
      }),
    ];
  }

  private seedHistoricoEAuditoria() {
    this.invoices.forEach((invoice) => {
      this.pushHistorico(invoice.idInspection, invoice.status);
      this.seedAuditoriaDaInvoice(invoice);
    });
  }

  private seedAuditoriaDaInvoice(invoice: NfseInvoiceModel) {
    const id = invoice.idInspection;
    const nfseId = invoice.idNfseInvoice;
    const docs = {
      pdfDisponivel: !!invoice.documentosDisponiveis?.pdfDisponivel,
      xmlDisponivel: !!invoice.documentosDisponiveis?.xmlDisponivel,
      numero: invoice.numero,
      protocolo: invoice.protocolo,
    };

    this.pushAuditoria(id, nfseId, "NaoIniciada", "EmissaoSolicitada", "Emissao", {
      usuarioResponsavel: "Sistema",
      perfilUsuario: "Sistema",
      dataHora: "2026-07-19T08:05:00.000Z",
      evidenciaAssociada: { etapa: "solicitacao-emissao" },
      retornoProvedor: JSON.stringify({
        mensagem: "Solicitação de emissão enviada ao provedor fiscal.",
        protocoloEnvio: invoice.protocolo || this.uuid(),
      }),
    });

    this.pushAuditoria(id, nfseId, "EmissaoSolicitada", invoice.status, "Emissao", {
      dataHora: invoice.dataEmissao || "2026-07-20T10:00:00.000Z",
      evidenciaAssociada: docs,
      retornoProvedor: JSON.stringify({
        status: invoice.status,
        numero: invoice.numero,
        codigoVerificacao: invoice.codigoVerificacao,
        mensagemRejeicao: invoice.mensagemRejeicao,
      }),
    });

    if (invoice.status === "Rejeitada" || invoice.status === "PendenteCorrecao") {
      this.pushAuditoria(id, nfseId, "EmissaoSolicitada", invoice.status, "Rejeicao", {
        usuarioResponsavel: "Sistema",
        perfilUsuario: "Sistema",
        dataHora: "2026-07-20T10:01:00.000Z",
        evidenciaAssociada: { codigoErro: "E001", campo: "tomador" },
        retornoProvedor: JSON.stringify({
          codigo: "E001",
          mensagem: invoice.mensagemRejeicao || "Rejeição retornada pelo provedor fiscal.",
          payloadBruto: "<Rejeicao><Codigo>E001</Codigo></Rejeicao>",
        }),
      });
    }

    if (invoice.status === "Autorizada" || invoice.status === "EnviadaPorEmail" || invoice.status === "Reenviada") {
      this.pushAuditoria(id, nfseId, "Autorizada", invoice.status, "Envio", {
        usuarioResponsavel: "Sistema",
        perfilUsuario: "Sistema",
        dataHora: "2026-07-20T10:05:00.000Z",
        evidenciaAssociada: { destinatario: invoice.tomador?.email, ...docs },
        retornoProvedor: JSON.stringify({
          destinatario: invoice.tomador?.email,
          statusEnvio: "enviado",
        }),
      });
    }

    if (invoice.status === "Reenviada") {
      this.pushAuditoria(id, nfseId, "EnviadaPorEmail", "Reenviada", "Reenvio", {
        dataHora: "2026-07-21T11:00:00.000Z",
        evidenciaAssociada: { destinatario: invoice.tomador?.email, ...docs },
        retornoProvedor: JSON.stringify({
          destinatario: invoice.tomador?.email,
          statusEnvio: "reenviado",
        }),
      });
    }

    if (invoice.status === "CancelamentoSolicitado" || invoice.status === "Cancelada") {
      this.pushAuditoria(id, nfseId, "Autorizada", invoice.status, "Cancelamento", {
        dataHora: invoice.dataCancelamento || "2026-07-21T09:00:00.000Z",
        evidenciaAssociada: { motivo: invoice.motivoCancelamento },
        retornoProvedor: JSON.stringify({
          motivo: invoice.motivoCancelamento,
          status: invoice.status,
        }),
      });
    }

    if (invoice.status === "FalhaIntegracao" || invoice.status === "EmReprocessamento") {
      this.pushAuditoria(id, nfseId, "EmissaoSolicitada", invoice.status, "Reprocessamento", {
        dataHora: "2026-07-20T11:00:00.000Z",
        evidenciaAssociada: { tentativa: 2 },
        retornoProvedor: JSON.stringify({
          erro: "timeout",
          mensagem: invoice.mensagemRejeicao,
        }),
      });
    }
  }

  private ok(body: unknown): Observable<HttpEvent<unknown>> {
    return of(new HttpResponse({ status: 200, body })).pipe(delay(300));
  }

  private notFound(): Observable<HttpEvent<unknown>> {
    return this.error(404, "Rota mock não encontrada.");
  }

  private error(status: number, title: string): Observable<HttpEvent<unknown>> {
    return throwError(
      () => new HttpErrorResponse({ status, error: { title, status } })
    ).pipe(delay(300));
  }

  private uuid(): string {
    return "mock-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}
